import React, { useCallback, useRef, forwardRef, useEffect } from 'react';
import { Text, Rect, Group } from 'react-konva';
import { Node } from '../../core/models/scene';
import { useThemeAwareColor } from '../../react-app/theme/ThemeProvider';

interface TextNodeProps {
  node: Node;
  selected: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: any) => void;
  onDoubleClick?: (e: any) => void;
  onStartTextEdit?: (textNode: any) => void;
  editing?: boolean;
  onTransformStart?: (e: any) => void;
  onTransformEnd?: (e: any) => void;
}

const TextNodeInner = ({
  node,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onDoubleClick,
  onStartTextEdit,
  editing,
  onTransformStart,
  onTransformEnd
}: TextNodeProps, ref: React.Ref<any>) => {
  const getThemeColor = useThemeAwareColor();
  const groupRef = useRef<any>(null);
  const textRef = useRef<any>(null);
  const boundsRef = useRef<any>(null);
  

  const handleDragStart = (e: any) => {
    const pos = e.target.position();
    onDragStart?.({ x: pos.x, y: pos.y });
  };

  const handleDragMove = (e: any) => {
    const pos = e.target.position();
    onDragMove?.({ x: pos.x, y: pos.y });
  };

  const handleDragEnd = (e: any) => {
    const pos = e.target.position();
    onDragEnd?.({ x: pos.x, y: pos.y });
  };

  const handleDoubleClick = (e: any) => {
    e.cancelBubble = true;
    onDoubleClick?.(e);
    if (onStartTextEdit && textRef.current) onStartTextEdit(textRef.current);
  };

  // Resolve style from node and attrs for compatibility with style commands
  const attrs: any = (node as any)?.attrs || {};
  const fontSize = attrs.fontSize ?? (node as any).fontSize ?? 16;
  const fontFamily = attrs.fontFamily ?? (node as any).fontFamily ?? 'Arial';
  const textColor = attrs.textColor ?? '#0F172A';
  const hAlign = (attrs.textAlign as any) || 'center';
  const vAlign = (attrs.verticalAlign as any) || 'middle';
  const rectFill = (node as any).fillColor && (node as any).fillColor !== 'transparent' ? (node as any).fillColor : 'rgba(0,0,0,0)';
  const rectStrokeColor = (() => {
    const c = (node as any).strokeColor ?? attrs.strokeColor;
    if (!c || c === 'transparent') return 'rgba(0,0,0,0)';
    return c as string;
  })();
  const rectStrokeWidth = typeof (node as any).strokeWidth === 'number' ? (node as any).strokeWidth : (typeof attrs.strokeWidth === 'number' ? attrs.strokeWidth : 0);
  const rectDash = Array.isArray(attrs.dash) ? attrs.dash : [];
  const rectDashEnabled = !!attrs.dashEnabled && rectDash.length > 0;

  // Auto-fit height to text content after text or width/style changes
  useEffect(() => {
    const t: any = textRef.current;
    const r: any = boundsRef.current;
    const g: any = groupRef.current;
    if (!t || !r || !g) return;

    try {
      const boxWidth = Math.max(20, (node.width as number) || 100);
      // Get current rendered height or fallback to stored height
      // On initial mount, bounds might not have height set yet, so use stored node.height
      const storedHeight = (node.height as number) || 30;
      const renderedHeight = typeof r.height === 'function' ? r.height() : null;
      const currentRenderedHeight = (typeof renderedHeight === 'number' && renderedHeight > 0) ? renderedHeight : storedHeight;
      const lineH = (t.lineHeight?.() || 1.2) * (fontSize as number);

      // Build a measuring canvas 2D context
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const fw = (attrs.fontWeight || '').toString().toLowerCase();
      const fs = (attrs.fontStyle || '').toString().toLowerCase();
      const isBold = fw === 'bold' || fw === '700';
      const isItalic = fs === 'italic';
      const fontStyle = `${isItalic ? 'italic ' : ''}${isBold ? '700 ' : '400 '}${fontSize}px ${fontFamily}`;
      ctx.font = fontStyle;

      const measure = (s: string) => ctx.measureText(s).width;

      const wrapParagraph = (para: string) => {
        // Break by words; fallback to char-wrap for very long tokens
        const words = para.split(/(\s+)/); // keep spaces tokens
        let current = '';
        let lines = 0;
        for (const token of words) {
          if (token === '') continue;
          const next = current + token;
          if (measure(next) <= boxWidth || current === '') {
            current = next;
          } else {
            // commit current line
            lines += 1;
            current = token.trimStart();
            // If single token is wider than box, break by characters
            if (measure(current) > boxWidth) {
              let buf = '';
              for (const ch of current) {
                const trial = buf + ch;
                if (measure(trial) <= boxWidth || buf === '') buf = trial;
                else { lines += 1; buf = ch; }
              }
              current = buf;
            }
          }
        }
        if (current.length > 0) lines += 1;
        return lines;
      };

      const paragraphs = (String(node.text || 'Text')).split('\n');
      let totalLines = 0;
      for (const p of paragraphs) {
        if (p.trim().length === 0) {
          // Empty line - still counts as one line
          totalLines += 1;
        } else {
          totalLines += wrapParagraph(p);
        }
      }
      // Add buffer that scales with font size to prevent clipping
      // Larger fonts need more buffer for descenders, subpixel rendering, line spacing, etc.
      // Use a more generous buffer: at least 6px or 10% of font size, whichever is larger
      const buffer = Math.max(6, Math.ceil(fontSize * 0.1) + 4);
      // Also ensure each line has adequate height - add extra space per line for larger fonts
      const perLineBuffer = totalLines > 1 ? Math.ceil(fontSize * 0.05) : 0;
      const contentHeight = Math.max(18, Math.ceil(totalLines * lineH) + buffer + (perLineBuffer * totalLines));
      
      // Always use at least contentHeight to prevent clipping (critical after refresh when stored height might be stale)
      // But preserve manual resizes if user made it larger (don't shrink)
      const required = Math.max(currentRenderedHeight, contentHeight);

      // Apply measured height back to text, bounds, and group
      const prevH = t.height();
      t.height(required);
      if (typeof r.height === 'function') r.height(required);
      if (typeof g.height === 'function') g.height(required);
      g.scale({ x: 1, y: 1 });
      if (required !== prevH) t.getLayer()?.batchDraw();
    } catch {}
  }, [node.text, node.width, node.height, fontSize, fontFamily, attrs.fontWeight, attrs.fontStyle, attrs.textDecoration]);

  return (
    <>
      <Group
        id={node.id}
        name="shape"
        ref={useCallback((n: any) => {
          groupRef.current = n;
          if (typeof ref === 'function') ref(n);
          else if (ref && typeof (ref as any) === 'object') (ref as any).current = n;
        }, [ref])}
        x={node.x}
        y={node.y}
        width={node.width || 100}
        height={node.height || 30}
        draggable={true}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={handleDragEnd}
        onClick={onClick}
        onDblClick={handleDoubleClick}
        onTransformStart={onTransformStart as any}
        onTransformEnd={onTransformEnd as any}
        onTransform={(e: any) => {
          const target = e.target;
          const scaleX = target.scaleX();
          const scaleY = target.scaleY();
          const curW = (boundsRef.current?.width?.() as number) || (node.width || 100);
          const curH = (boundsRef.current?.height?.() as number) || (node.height || 30);
          const nextW = Math.max(20, curW * scaleX);
          const nextH = Math.max(18, curH * scaleY);
          // Apply live size updates to bounds + text
          try { boundsRef.current?.width?.(nextW); } catch {}
          try { boundsRef.current?.height?.(nextH); } catch {}
          try { textRef.current?.width?.(nextW); } catch {}
          try { textRef.current?.height?.(nextH); } catch {}
          try { groupRef.current?.width?.(nextW); } catch {}
          try { groupRef.current?.height?.(nextH); } catch {}
          // Keep group scale at 1 so text glyphs don't scale; size is applied above
          try { target.scaleX(1); target.scaleY(1); } catch {}
        }}
      >
        {/* Bounding rectangle (transformable) */}
        <Rect
          x={0}
          y={0}
          width={node.width || 100}
          height={node.height || 30}
          name="text-bounds"
          ref={boundsRef}
          listening={false}
          fill={rectFill}
          stroke={rectStrokeWidth > 0 ? rectStrokeColor : 'rgba(0,0,0,0)'}
          strokeWidth={rectStrokeWidth}
          strokeEnabled={rectStrokeWidth > 0}
          dashEnabled={rectDashEnabled}
          dash={rectDashEnabled ? rectDash : undefined}
        />
        
        {/* Text */}
        <Text
          ref={textRef}
          text={node.text || 'Text'}
          fontSize={fontSize}
          fontFamily={fontFamily}
          fill={getThemeColor(textColor, { type: 'foreground' })}
          fontStyle={(() => {
            const fw = (attrs.fontWeight || '').toString().toLowerCase();
            const fs = (attrs.fontStyle || '').toString().toLowerCase();
            const isBold = fw === 'bold';
            const isItalic = fs === 'italic';
            if (isBold && isItalic) return 'bold italic';
            if (isBold) return 'bold';
            if (isItalic) return 'italic';
            return 'normal';
          })()}
          textDecoration={attrs.textDecoration || 'none'}
          width={node.width || 100}
          height={node.height || 30}
          align={hAlign}
          verticalAlign={vAlign}
          visible={!editing}
          wrap="word"
        />
      </Group>
    </>
  );
};

export const TextNode = forwardRef<any, TextNodeProps>(TextNodeInner);
